import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Typography,
  Popover,
  Container,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../contexts/AuthContext";
import invoiceV2ApiService, {
  InvoiceV2Request,
} from "../../services/invoice-v2-api";
import userManagementApiService from "../../services/user-management-api";
import { Employee } from "../../types/leave";
import { formatDate } from "../../services/dateTimeService";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DescriptionIcon from "@mui/icons-material/Description";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import DeleteIcon from "@mui/icons-material/Delete";
import Tooltip from "@mui/material/Tooltip";
import PdfViewer from "../../components/shared/PdfViewer";
import ImageViewer from "../../components/shared/ImageViewer";
import SearchIcon from "@mui/icons-material/Search";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import InfoIcon from "@mui/icons-material/Info";
import { getCurrencyByCode } from "../../utils/currencyData";

interface InvoiceV2ProcessingPageProps {
  onStatusChange?: () => void;
}

/**
 * Invoice V2 Processing Page (Admin Only)
 * Allows admins to view, filter, search, and update status of all invoice V2 requests
 */
const InvoiceV2ProcessingPage: React.FC<InvoiceV2ProcessingPageProps> = ({
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [requests, setRequests] = useState<InvoiceV2Request[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [employees, setEmployees] = useState<Employee[]>([]);

  // Helper function to get current month date range
  const getDefaultDateRange = () => {
    const today = new Date();
    const firstDay = new Date(today.getFullYear(), today.getMonth(), 1);
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    return {
      start: firstDay.toISOString().split("T")[0],
      end: lastDay.toISOString().split("T")[0],
    };
  };

  const defaultDates = getDefaultDateRange();

  // Filter states
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [employeeFilter, setEmployeeFilter] = useState("");
  const [startDate, setStartDate] = useState(defaultDates.start);
  const [endDate, setEndDate] = useState(defaultDates.end);

  // Temporary filter states for UI (not triggering API calls)
  const [tempSearchText, setTempSearchText] = useState("");
  const [tempStatusFilter, setTempStatusFilter] = useState("");
  const [tempEmployeeFilter, setTempEmployeeFilter] = useState("");
  const [tempStartDate, setTempStartDate] = useState(defaultDates.start);
  const [tempEndDate, setTempEndDate] = useState(defaultDates.end);

  // Status update popover
  const [statusPopoverAnchor, setStatusPopoverAnchor] =
    useState<HTMLElement | null>(null);
  const [selectedRequest, setSelectedRequest] =
    useState<InvoiceV2Request | null>(null);
  const [newStatus, setNewStatus] = useState("");

  // Rejection comment dialog
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionComment, setRejectionComment] = useState("");
  const [rejectionError, setRejectionError] = useState("");

  // Delete dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);

  // PDF Viewer state
  const [pdfViewerOpen, setPdfViewerOpen] = useState(false);
  const [selectedPdfUrl, setSelectedPdfUrl] = useState<string>("");
  const [selectedPdfFileName, setSelectedPdfFileName] = useState<string>("");
  // Image Viewer state
  const [imageViewerOpen, setImageViewerOpen] = useState(false);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string>("");
  const [selectedImageFileName, setSelectedImageFileName] =
    useState<string>("");
  const [viewableFiles, setViewableFiles] = useState<Record<string, boolean>>(
    {},
  );

  const isFileViewable = (fileName: string): boolean => {
    if (!fileName) return false;
    const extension = fileName.toLowerCase().split(".").pop();
    // Allow PDF, JPEG, and PNG files to be viewed. Disable only for ZIP
    return (
      extension === "pdf" ||
      extension === "jpg" ||
      extension === "jpeg" ||
      extension === "png"
    );
  };

  const checkFileViewability = async (requests: InvoiceV2Request[]) => {
    const viewability: Record<string, boolean> = {};
    for (const request of requests) {
      try {
        const metadata = await invoiceV2ApiService.getAttachmentMetadata(
          request.requestId || "",
        );
        viewability[request.requestId || ""] = isFileViewable(
          metadata.fileName,
        );
      } catch (err) {
        // If we can't get metadata, assume not viewable
        viewability[request.requestId || ""] = false;
      }
    }
    setViewableFiles(viewability);
  };

  // Fetch requests with filters and employees on component mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all employees for filter dropdown
        const allEmployees = await userManagementApiService.getAllUsers(
          0,
          1000,
        );
        setEmployees(allEmployees.content);
      } catch (err) {
        const errorMessage =
          err instanceof Error
            ? err.message
            : t("error.failedToFetchEmployees");
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Fetch requests with current filters
  useEffect(() => {
    const fetchFilteredRequests = async () => {
      try {
        setLoading(true);
        setError(null);

        const filteredRequests =
          await invoiceV2ApiService.getInvoiceRequestsWithFilters(
            startDate || undefined,
            endDate || undefined,
            statusFilter || undefined,
            employeeFilter ? parseInt(employeeFilter) : undefined,
            searchText || undefined,
          );

        setRequests(filteredRequests);
        await checkFileViewability(filteredRequests);
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : t("error.failedToFetchRequests");
        setError(errorMessage);
      } finally {
        setLoading(false);
      }
    };

    fetchFilteredRequests();
  }, [searchText, statusFilter, employeeFilter, startDate, endDate]);

  // Initialize temp filters when component mounts
  useEffect(() => {
    setTempSearchText(searchText);
    setTempStatusFilter(statusFilter);
    setTempEmployeeFilter(employeeFilter);
    setTempStartDate(startDate);
    setTempEndDate(endDate);
  }, []);

  // Handle search button click
  const handleSearch = () => {
    setSearchText(tempSearchText);
    setStatusFilter(tempStatusFilter);
    setEmployeeFilter(tempEmployeeFilter);
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  // Handle reset button click
  const handleReset = () => {
    setTempSearchText("");
    setTempStatusFilter("");
    setTempEmployeeFilter("");
    setTempStartDate(defaultDates.start);
    setTempEndDate(defaultDates.end);
    setSearchText("");
    setStatusFilter("");
    setEmployeeFilter("");
    setStartDate(defaultDates.start);
    setEndDate(defaultDates.end);
  };

  // Check if temp filters differ from applied filters (for Apply button)
  const isDirty =
    tempSearchText !== searchText ||
    tempStatusFilter !== statusFilter ||
    tempEmployeeFilter !== employeeFilter ||
    tempStartDate !== startDate ||
    tempEndDate !== endDate;

  // Check if applied filters differ from default values (for Reset button)
  const isNotDefault =
    searchText !== "" ||
    statusFilter !== "" ||
    employeeFilter !== "" ||
    startDate !== defaultDates.start ||
    endDate !== defaultDates.end;

  const handleStatusChange = async (status: string) => {
    if (!selectedRequest) return;

    // If status is REJECTED, show rejection comment dialog
    if (status === "REJECTED") {
      setStatusPopoverAnchor(null);
      setRejectionDialogOpen(true);
      setRejectionComment("");
      setRejectionError("");
      return;
    }

    try {
      setLoading(true);
      await invoiceV2ApiService.updateInvoiceRequestStatus(
        selectedRequest.requestId || "",
        status,
        user?.username || "",
      );
      setStatusPopoverAnchor(null);
      setSelectedRequest(null);
      setNewStatus("");
      // Refresh the list
      const filteredRequests =
        await invoiceV2ApiService.getInvoiceRequestsWithFilters(
          startDate || undefined,
          endDate || undefined,
          statusFilter || undefined,
          employeeFilter ? parseInt(employeeFilter) : undefined,
          searchText || undefined,
        );
      setRequests(filteredRequests);

      // Trigger badge refresh in parent component
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("error.failedToUpdateStatus");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleRejectionSubmit = async () => {
    if (!selectedRequest) return;

    // Validate rejection comment
    if (!rejectionComment.trim()) {
      setRejectionError(t("invoice.v2.rejectionCommentRequired"));
      return;
    }

    try {
      setLoading(true);
      await invoiceV2ApiService.updateInvoiceRequestStatus(
        selectedRequest.requestId || "",
        "REJECTED",
        user?.username || "",
        rejectionComment,
      );
      setRejectionDialogOpen(false);
      setSelectedRequest(null);
      setNewStatus("");
      setRejectionComment("");
      setRejectionError("");
      // Refresh the list
      const filteredRequests =
        await invoiceV2ApiService.getInvoiceRequestsWithFilters(
          startDate || undefined,
          endDate || undefined,
          statusFilter || undefined,
          employeeFilter ? parseInt(employeeFilter) : undefined,
          searchText || undefined,
        );
      setRequests(filteredRequests);

      // Trigger badge refresh in parent component
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("error.failedToUpdateStatus");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleStatusPopoverOpen = (
    event: React.MouseEvent<HTMLElement>,
    request: InvoiceV2Request,
  ) => {
    setStatusPopoverAnchor(event.currentTarget);
    setSelectedRequest(request);
    setNewStatus(request.status || "");
  };

  const handleStatusPopoverClose = () => {
    setStatusPopoverAnchor(null);
    setSelectedRequest(null);
    setNewStatus("");
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "SUBMITTED":
        return "info";
      case "PROCESSING":
        return "warning";
      case "PROCESSED":
        return "success";
      case "REJECTED":
        return "error";
      default:
        return "default";
    }
  };

  const getEmployeeName = (employeeId: number) => {
    const employee = employees.find((e) => e.id === employeeId);
    return employee
      ? employee.firstName + " " + employee.lastName
      : t("common.unknown");
  };

  const handleViewFile = async (requestId: string) => {
    try {
      // Get the file metadata to determine file type
      const metadata =
        await invoiceV2ApiService.getAttachmentMetadata(requestId);
      const fileUrl = `/ofc/invoice/v2/download/${requestId}`;
      const extension = metadata.fileName.toLowerCase().split(".").pop();

      if (extension === "pdf") {
        // Open PDF in PDF viewer
        setSelectedPdfUrl(fileUrl);
        setSelectedPdfFileName(metadata.fileName);
        setPdfViewerOpen(true);
      } else if (["jpg", "jpeg", "png"].includes(extension || "")) {
        // Open image in image viewer
        setSelectedImageUrl(fileUrl);
        setSelectedImageFileName(metadata.fileName);
        setImageViewerOpen(true);
      }
    } catch (err) {
      setError(t("error.failedToViewAttachment"));
      console.error(t("error.failedToViewAttachment"), err);
    }
  };

  const handleDownload = async (requestId: string) => {
    try {
      // Get the original filename first
      const metadata =
        await invoiceV2ApiService.getAttachmentMetadata(requestId);
      const blob = await invoiceV2ApiService.downloadAttachment(requestId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      // Use the original filename from metadata
      link.download = metadata.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t("error.failedToDownloadAttachment"));
      console.error("Failed to download attachment:", err);
    }
  };

  const handleDownloadOptionalAttachment = async (requestId: string) => {
    try {
      // Get the original filename first
      const metadata =
        await invoiceV2ApiService.getOptionalAttachmentMetadata(requestId);
      const blob =
        await invoiceV2ApiService.downloadOptionalAttachment(requestId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = metadata.fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t("error.failedToDownloadAttachment"));
      console.error("Failed to download optional attachment:", err);
    }
  };

  const handleDeleteClick = (requestId: string) => {
    setRequestToDelete(requestId);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!requestToDelete) return;
    try {
      setLoading(true);
      await invoiceV2ApiService.deleteInvoiceRequest(
        requestToDelete,
        user?.username || "",
      );
      setDeleteDialogOpen(false);
      setRequestToDelete(null);
      // Refresh the list
      const filteredRequests =
        await invoiceV2ApiService.getInvoiceRequestsWithFilters(
          startDate || undefined,
          endDate || undefined,
          statusFilter || undefined,
          employeeFilter ? parseInt(employeeFilter) : undefined,
          searchText || undefined,
        );
      setRequests(filteredRequests);
      if (onStatusChange) {
        onStatusChange();
      }
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : t("error.failedToDeleteRequest");
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {t("invoice.v2.processing")}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label={t("users.startDate")}
              value={tempStartDate}
              onChange={(e) => setTempStartDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <TextField
              fullWidth
              type="date"
              label={t("leave.endDate")}
              value={tempEndDate}
              onChange={(e) => setTempEndDate(e.target.value)}
              size="small"
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("common.status")}</InputLabel>
              <Select
                value={tempStatusFilter}
                onChange={(e) => setTempStatusFilter(e.target.value)}
                label={t("common.status")}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                <MenuItem value="SUBMITTED">SUBMITTED</MenuItem>
                <MenuItem value="PROCESSING">PROCESSING</MenuItem>
                <MenuItem value="PROCESSED">PROCESSED</MenuItem>
                <MenuItem value="REJECTED">REJECTED</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth size="small">
              <InputLabel>{t("leave.employee")}</InputLabel>
              <Select
                value={tempEmployeeFilter}
                onChange={(e) => setTempEmployeeFilter(e.target.value)}
                label={t("leave.employee")}
              >
                <MenuItem value="">{t("common.all")}</MenuItem>
                {employees.map((emp) => (
                  <MenuItem key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 2.5 }}>
            <TextField
              fullWidth
              placeholder={t("invoice.processing.searchPlaceholder")}
              value={tempSearchText}
              onChange={(e) => setTempSearchText(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon sx={{ mr: 1, color: "text.secondary" }} />
                ),
              }}
            />
          </Grid>

          <Grid
            size={{ xs: 12, sm: 6, md: 1.5 }}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: 1,
            }}
          >
            <Button
              variant="contained"
              onClick={handleSearch}
              disabled={!isDirty}
              size="small"
            >
              {t("common.apply")}
            </Button>
            <Tooltip title={t("common.reset")}>
              <span>
                <IconButton
                  onClick={handleReset}
                  disabled={!isNotDefault}
                  color="primary"
                  size="small"
                >
                  <RestartAltIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      {/* Requests Table */}
      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t("invoice.v2.requestId")}</TableCell>
                <TableCell>{t("leave.employee")}</TableCell>
                <TableCell>{t("invoice.v2.type")}</TableCell>
                <TableCell>{t("invoice.v2.notes")}</TableCell>
                <TableCell align="right">{t("invoice.v2.amount")}</TableCell>
                <TableCell>{t("invoice.v2.date")}</TableCell>
                <TableCell>{t("invoice.v2.status")}</TableCell>
                <TableCell align="center"></TableCell>
                <TableCell align="center"></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {requests.map((request) => (
                <TableRow key={request.id}>
                  <TableCell>{request.requestId}</TableCell>
                  <TableCell>
                    {getEmployeeName(request.employeeId || 0)}
                  </TableCell>
                  <TableCell>{request.submissionType}</TableCell>
                  <TableCell>
                    {request.submissionType === "FILE_UPLOAD" && request.notes
                      ? request.notes
                      : "-"}
                  </TableCell>
                  <TableCell align="right">
                    {(() => {
                      const currency = request.items?.[0]?.currency || "JPY";
                      const currencyInfo = getCurrencyByCode(currency);
                      const symbol = currencyInfo?.symbol || currency;
                      return `${symbol}${request.totalAmount.toLocaleString()}`;
                    })()}
                  </TableCell>
                  <TableCell>{formatDate(request.submissionDate)}</TableCell>
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      <Chip
                        label={request.status}
                        color={getStatusColor(request.status || "")}
                        size="small"
                        onClick={(e) => handleStatusPopoverOpen(e, request)}
                        clickable
                      />
                      {request.status === "REJECTED" &&
                        request.rejectionComment && (
                          <Tooltip title={request.rejectionComment}>
                            <InfoIcon
                              sx={{
                                fontSize: 18,
                                color: "error.main",
                                cursor: "pointer",
                              }}
                            />
                          </Tooltip>
                        )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 0.5 }}>
                      {/* View Button */}
                      {request.submissionType === "CREATE_REQUEST" && (
                        <Tooltip
                          title={
                            viewableFiles[request.requestId || ""]
                              ? t("common.view")
                              : t("invoice.v2.viewNotAvailable")
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleViewFile(request.requestId || "")
                              }
                              color="primary"
                              disabled={!viewableFiles[request.requestId || ""]}
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      )}

                      {/* Download PDF Button */}
                      {request.submissionType === "CREATE_REQUEST" && (
                        <Tooltip title={t("invoice.v2.downloadPdf")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(request.requestId || "")
                            }
                            color="primary"
                          >
                            <PictureAsPdfIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Download Optional Attachment Button */}
                      {request.submissionType === "CREATE_REQUEST" &&
                        request.optionalAttachmentBlobId && (
                          <Tooltip
                            title={t("invoice.v2.downloadOptionalAttachment")}
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleDownloadOptionalAttachment(
                                  request.requestId || "",
                                )
                              }
                              color="primary"
                            >
                              <AttachFileIcon />
                            </IconButton>
                          </Tooltip>
                        )}

                      {/* Download File Button for FILE_UPLOAD */}
                      {request.submissionType === "FILE_UPLOAD" && (
                        <Tooltip title={t("invoice.v2.downloadFile")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              handleDownload(request.requestId || "")
                            }
                            color="primary"
                          >
                            <AttachFileIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Delete Button - Only for SUBMITTED or REJECTED status */}
                      {(request.status === "SUBMITTED" ||
                        request.status === "REJECTED") && (
                        <Tooltip title={t("common.delete")}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              handleDeleteClick(request.requestId || "")
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Status Update Popover */}
      <Popover
        open={Boolean(statusPopoverAnchor)}
        anchorEl={statusPopoverAnchor}
        onClose={handleStatusPopoverClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <Box sx={{ p: 2, minWidth: 200 }}>
          <Typography variant="caption" sx={{ display: "block", mb: 1 }}>
            {t("reimbursement.v2.processing.currentStatus")}:{" "}
            {selectedRequest?.status}
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {["SUBMITTED", "PROCESSING", "PROCESSED", "REJECTED"].map(
              (status) => (
                <Button
                  key={status}
                  size="small"
                  variant={
                    selectedRequest?.status === status
                      ? "contained"
                      : "outlined"
                  }
                  onClick={() => handleStatusChange(status)}
                  disabled={loading}
                  fullWidth
                >
                  {status}
                </Button>
              ),
            )}
          </Box>
        </Box>
      </Popover>

      {/* PDF Viewer Modal */}
      <PdfViewer
        open={pdfViewerOpen}
        onClose={() => setPdfViewerOpen(false)}
        pdfUrl={selectedPdfUrl}
        fileName={selectedPdfFileName}
      />

      {/* Image Viewer Modal */}
      <ImageViewer
        open={imageViewerOpen}
        onClose={() => setImageViewerOpen(false)}
        imageUrl={selectedImageUrl}
        fileName={selectedImageFileName}
      />

      {/* Rejection Comment Dialog */}
      <Dialog
        open={rejectionDialogOpen}
        onClose={() => {
          setRejectionDialogOpen(false);
          setRejectionComment("");
          setRejectionError("");
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t("invoice.v2.rejectWithComment")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label={t("invoice.v2.rejectionComment")}
            placeholder={t("invoice.v2.rejectionCommentPlaceholder")}
            fullWidth
            multiline
            rows={4}
            value={rejectionComment}
            onChange={(e) => {
              setRejectionComment(e.target.value);
              setRejectionError("");
            }}
            error={!!rejectionError}
            helperText={rejectionError}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setRejectionDialogOpen(false);
              setRejectionComment("");
              setRejectionError("");
            }}
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleRejectionSubmit}
            variant="contained"
            color="error"
            disabled={loading}
          >
            {t("common.reject")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>{t("common.confirmDelete")}</DialogTitle>
        <DialogContent>{t("invoice.v2.deleteConfirmation")}</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            disabled={loading}
          >
            {t("common.delete")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InvoiceV2ProcessingPage;
